import packageJSON from '../../package.json';
import { RootState } from '../app/store';
import { AgentTypes } from '../constants/event';
import { TranscriptTypes } from '../constants/enums';
import {
  IssueReportCarts,
  IssueReportContext,
  IssueReportEnvelopeIdentity,
  IssueReportPosContext,
  IssueReportTranscriptLine,
} from '../redux/features/issueReport/issueReport.props';
import { selectCurrentTask } from '../redux/features/taskRouter/taskRouter.selector';
import { selectTranscriptMessages } from '../selectors/message';
import { IBrandMenuOrderItem, ICartError } from '../types/brandMenu';
import { getConsolidatedMenuBrand } from './restaurants';
import { buildUnifiedMenuModuleOrder } from './menuUnification';

/**
 * HITL-1492 Pillar 3, Part A — build the frozen issue-report context.
 *
 * PURE and SYNCHRONOUS: no `await`, no network, no DOM. That is what satisfies
 * NFR-1 — the press handler does O(1) work and the flag never costs a frame.
 *
 * Captured ONCE on press and never re-read. That is mandatory, not stylistic:
 * `navigateToTaskRouterPage` dispatches closeConnection(), resetAI() and
 * resetSelectedRestaurantDetails() before navigating, so by the time a
 * classification is submitted at the task-router boundary the socket is closed and
 * store_code is gone from Redux.
 */

/** Tail of the rendered transcript: 5 turns either side plus the newest line. */
export const TRANSCRIPT_WINDOW_TURNS = 5;
/** Per-line character cap. Longer payloads are truncated. */
export const TRANSCRIPT_LINE_CHAR_CAP = 500;
/** Total character budget across the window. */
export const TRANSCRIPT_TOTAL_CHAR_CAP = 8 * 1024;
/** Per-cart item cap. */
export const CART_ITEM_CAP = 60;
/** Cap on `pos.cart_errors`, mirroring how the carts are bounded. */
export const CART_ERROR_CAP = 20;

/**
 * The POS check-item keys worth shipping.
 *
 * `checkEventItems` is POSMON's raw shape — ~24 keys per item, most of them empty
 * scaffolding (hold_uid, hold_expire_time, memo_id, screen_group_id,
 * price_description_id, custom_request_pos_id, source_check_id). A six-item order is
 * ~150 keys of mostly nothing and it is the largest block in the payload.
 *
 * This is a KEY PROJECTION, not a transformation: values are copied verbatim, so the
 * decision to diff the two carts server-side is untouched. Unknown keys are dropped
 * rather than preserved — if a diff later needs one, add it here.
 */
export const POS_CART_PROJECTED_KEYS = [
  'name',
  'pos_id',
  'price',
  'tax',
  'is_combo',
  'is_modifier',
  'is_coupon',
  'is_86ed',
] as const;

export interface BuildIssueReportContextDeps {
  /**
   * Injected so every derived duration in the payload is deterministic under test,
   * and so the builder is a pure function of its arguments rather than of the wall
   * clock.
   */
  nowMs?: number;
}

const toEpoch = (iso?: string | null): number | null => {
  if (!iso) return null;
  const epoch = new Date(iso).getTime();
  return Number.isNaN(epoch) ? null : epoch;
};

const projectPosItem = (item: any): Record<string, unknown> =>
  POS_CART_PROJECTED_KEYS.reduce((acc, key) => {
    if (item && key in item) {
      acc[key] = item[key];
    }
    return acc;
  }, {} as Record<string, unknown>);

/**
 * The tail of the transcript AS THE AGENT SAW IT, capped per line and in total.
 *
 * The ORDERBOARD + truthy-payload gate mirrors MessageContainer exactly. Without it
 * the payload carries lines that were never rendered: HITL sends and RESTAURANTSTAFF
 * TTS both land in `finalTextFrames`, and stores running `flush_on_tts` emit an empty
 * ORDERBOARD final ~0.2-0.6s after every agent TTS. Promoted lines survive the gate
 * because `promotedEventToFrame` normalises `agent_type` and `data.status` onto the
 * synthesized frame for exactly this reason.
 *
 * Trimmed rather than raw because this is the largest variable-length block in the
 * payload and the pagehide path shares a 64 KB keepalive budget across all in-flight
 * requests.
 */
const buildTranscript = (messages: any[]): IssueReportTranscriptLine[] => {
  const rendered = messages.filter(
    (frame) =>
      frame?.agent_type === AgentTypes.orderBoard && frame?.data?.payload
  );
  const window = rendered.slice(-(TRANSCRIPT_WINDOW_TURNS * 2 + 1));
  let budget = TRANSCRIPT_TOTAL_CHAR_CAP;
  const lines: IssueReportTranscriptLine[] = [];

  // Newest-first so the budget is spent on the lines nearest the flag, then restore
  // chronological order — a window whose OLDEST lines survive is the wrong window.
  for (let i = window.length - 1; i >= 0; i -= 1) {
    if (budget <= 0) break;
    const frame = window[i];
    const rawPayload: string = frame.data.payload;
    const perLineCap = Math.min(TRANSCRIPT_LINE_CHAR_CAP, budget);
    const message = rawPayload.slice(0, perLineCap);
    budget -= message.length;

    lines.unshift({
      // Mirrors MessageContainer's own branch: a TTS frame is what came out of the
      // speaker, anything else is the customer.
      type:
        frame.data.status === 'TTS' ? TranscriptTypes.AI : TranscriptTypes.CX,
      timestamp: frame.timestamp ?? '',
      message,
      is_promoted: frame.isPromoted === true,
    });
  }

  return lines;
};

const buildCarts = (state: RootState): IssueReportCarts => {
  const cartItems = state.cart?.cartItems || {};
  const cartItemsQuantity = state.cart?.cartItemsQuantity || {};

  // Same converter the brand-menu order submission and the agent_cart event use, so
  // the snapshot is 1:1 with a submitted order.
  const agentCartAll = Object.values(cartItems)
    .map((item: any) => buildUnifiedMenuModuleOrder(item, cartItemsQuantity))
    .filter((item): item is IBrandMenuOrderItem => Boolean(item));
  const posCartAll: any[] = state.order?.checkEventItems || [];

  return {
    agent_cart: agentCartAll.slice(0, CART_ITEM_CAP),
    pos_cart: posCartAll.slice(0, CART_ITEM_CAP).map(projectPosItem),
    truncated:
      agentCartAll.length > CART_ITEM_CAP || posCartAll.length > CART_ITEM_CAP,
  };
};

/**
 * POS state, flat.
 *
 * `order_round_trip_ms` is the order -> check round trip once the check has landed,
 * otherwise how long the order has been outstanding. `order_status` is what tells
 * those apart: a `PENDING` status means the number is still climbing.
 */
const buildPos = (state: RootState, nowMs: number): IssueReportPosContext => {
  const { requestsById = {}, requestsOrder = [] } = state.order || ({} as any);
  const lastRequestId = requestsOrder.length
    ? requestsOrder[requestsOrder.length - 1]
    : '';
  const entry: any = lastRequestId ? requestsById[lastRequestId] : undefined;

  const sentAt = toEpoch(entry?.orderSentAt);
  const checkReceivedAt = toEpoch(entry?.checkResponse?.timestamp);
  const cartErrors: ICartError[] = state.order?.menuModuleCartErrors || [];

  return {
    latest_order_id: entry?.orderId ?? null,
    order_status: state.order?.finalOrderStatus ?? null,
    order_error: state.order?.orderError || null,
    order_round_trip_ms:
      sentAt === null ? null : Math.max(0, (checkReceivedAt ?? nowMs) - sentAt),
    cart_errors: cartErrors.slice(0, CART_ERROR_CAP),
  };
};

/**
 * Store CONFIGURATION, not live control state.
 *
 * Says strictly more than the `isUnsupervisedAi` boolean it replaces: it separates
 * "this restaurant has no AI at all" from "AI runs with a human watching". Compare
 * `ai.is_ai_auto_mode`, which is who is driving right now — `supervised` with
 * `is_ai_auto_mode: false` is a takeover in progress.
 */
const resolveAiMode = (
  state: RootState
): 'pure_hitl' | 'supervised' | 'unsupervised' => {
  if (!state.restaurant?.isAIEnabled) return 'pure_hitl';
  return state.restaurant?.selectedRestaurantDetails?.isUnsupervisedAi
    ? 'unsupervised'
    : 'supervised';
};

/**
 * The envelope identity, frozen at press time.
 *
 * `session_id` mirrors messagingTransformMiddleware's OWN fallback chain rather than
 * using selectSessionId, which is only step 2 of it — the chain below is the one that
 * actually stamps outbound events.
 *
 * Frozen rather than read live at send time because navigateToTaskRouterPage wipes all
 * three before a task-router-boundary classification is submitted. Kept out of the
 * emitted `context` on purpose: these become envelope fields, so duplicating them in
 * the payload would send each one twice.
 */
export const buildIssueReportEnvelopeIdentity = (
  state: RootState
): IssueReportEnvelopeIdentity => ({
  session_id:
    state.taskRouter?.taskAssignedSessionId ||
    state.sessionBoundary?.sessionId ||
    state.sessionBoundary?.endedSessionId ||
    state.messages?.currentSessionId ||
    null,
  restaurant_code:
    state.restaurant?.selectedRestaurantDetails?.restaurantCode || null,
  lane_id: state.taskRouter?.taskAssignedLaneId || null,
});

export const buildIssueReportContext = (
  state: RootState,
  deps: BuildIssueReportContextDeps = {}
): IssueReportContext => {
  const nowMs = deps.nowMs ?? Date.now();

  // What the agent is actually looking at: this selector already swaps customer
  // lines to `asr_promoted_text` when secondary ASR is on, so the window is the
  // rendered transcript rather than a second reconstruction of it.
  const messages: any[] = selectTranscriptMessages(state) || [];

  const restaurantDetails = state.restaurant?.selectedRestaurantDetails;
  const restaurantCode = restaurantDetails?.restaurantCode || '';
  const primaryRestaurantCode = restaurantDetails?.primaryRestaurantCode;

  return {
    session: {
      session_status: state.sessionBoundary?.sessionStatus ?? '',
      session_start_reason: state.sessionBoundary?.sessionStartReason ?? '',
      session_end_reason: state.sessionBoundary?.sessionEndReason ?? '',
      is_car_present: Boolean(state.messages?.isCarPresent),
    },
    restaurant: {
      ...(primaryRestaurantCode && primaryRestaurantCode !== restaurantCode
        ? { primary_restaurant_code: primaryRestaurantCode }
        : null),
      // DERIVED. null means EITHER "no brand matched" OR "restaurant is excluded" —
      // an excluded DQ store reports null, so reading that as "not DQ" is wrong.
      brand: getConsolidatedMenuBrand(
        restaurantCode,
        state.config?.CONSOLIDATED_MENU_BRANDS || [],
        state.config?.CONSOLIDATED_MENU_EXCLUDED_RESTAURANTS || []
      ),
      ai_mode: resolveAiMode(state),
      timezone: restaurantDetails?.timezone ?? null,
    },
    // ACTIVE language, not the store's bilingual capability: for an ASR or audio
    // report the language actually being spoken is the field that matters.
    language: state.language?.currentLanguage ?? '',
    // `currentTaskId`, NOT selectAssignedTaskId — the latter matches only the exact
    // `task_assigned` event and goes null one rung later at `task_assigned_ack`,
    // i.e. on nearly every real task-router report. Normalised from '' to null.
    task_id: selectCurrentTask(state) || null,
    ai: {
      // READ ONLY. Mutating isAIAutoMode to fix a display symptom caused a prod
      // incident: dropped orders and reverted carts (MR 1534).
      is_ai_auto_mode: Boolean(state.ai?.isAIAutoMode),
      // The colour only (AIStatusColors: green|yellow|orange|red). `aiStatus` is
      // `{ status, message }`; the message is the display string that says why.
      ai_status: state.ai?.aiStatus?.status ?? null,
      ai_status_message: state.ai?.aiStatus?.message || null,
      is_escalation: Boolean(state.ai?.isAIEscalation),
      is_escalated_on_car_enter: Boolean(state.ai?.isAIEscalatedOnCarEnter),
      escalation_reason: state.ai?.aiEscalationReason ?? null,
      requires_takeover: Boolean(state.ai?.requiresTakeover),
      express_mode_active: Boolean(state.ai?.expressModeActive),
    },
    menu: {
      is_brand_menu: Boolean(state.menu?.brandMenu),
      brand_menu_version:
        state.menu?.loadedVersionDetails?.brandMenuVersion ?? null,
      prp_menu_version:
        state.menu?.loadedVersionDetails?.prpMenuVersion ?? null,
    },
    pos: buildPos(state, nowMs),
    carts: buildCarts(state),
    transcript: buildTranscript(messages),
    ws: {
      event_reconnect_attempts: state.messages?.wsReconnectAttempts?.event ?? 0,
      audio_reconnect_attempts: state.messages?.wsReconnectAttempts?.audio ?? 0,
    },
  };
};

export const ISSUE_REPORT_HITL_BUILD = packageJSON.version;
